import { IncomingMessage } from "http";

import { GrowthBook } from "@growthbook/growthbook";
import * as JWT from "@tsndr/cloudflare-worker-jwt";
import cookie from "cookie";
import { GetServerSidePropsContext } from "next";
import Head from "next/head";
import Link from "next/link";
import { useTranslations, useFormatter } from "next-intl";

import { PoweredBy } from "@shared/components";
import { urls } from "@shared/constants";
import { Button, Container, Text } from "@shared/elements";
import { graphqlFetch } from "@shared/hooks";
import { docTitle } from "@shared/utils";

import {
  CreateSubmission,
  GetCallAccess,
  GetCallBySlug,
  TCreateSubmission,
  TGetCallAccess,
  TGetCallBySlug,
} from "__generated__/types";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";
import { checkIsCallEditable } from "src/utils/call";

// Fetch features from GrowthBook API and cache in memory
// TODO: update growthbook to use the new features method instead of this
let features: Array<Record<string, unknown>> | null = null;
let lastFetch = 0;
let fetchPromise: Promise<void> | null = null;
async function getFeatures() {
  if (Date.now() - lastFetch > 10000) {
    const url = process.env.NEXT_PUBLIC_GROWTHBOOK_FEATURES_ENDPOINT;
    if (!url) {
      console.error("GrowthBook features endpoint not set");
      return {};
    }
    lastFetch = Date.now();
    fetchPromise = fetch(url)
      .then((res) => res.json())
      .then((json) => {
        features = json.features || features;
      })
      .catch((e) => console.error("Error fetching features", e));
  }
  // Await the in-flight fetch so concurrent callers don't get empty features
  // while the first request is still fetching.
  if (!features && fetchPromise) await fetchPromise;
  return features || {};
}

function getCurrentUrl(context: GetServerSidePropsContext) {
  const req = context.req as IncomingMessage;

  // Determine the protocol (considering Vercel's x-forwarded-proto header)
  const protocol = req.headers["x-forwarded-proto"] || "http";

  // Retrieve the host
  const host = req.headers.host;

  // `context.resolvedUrl` gives us a cleaner version of the URL without
  // the "_next/data" prefix (used during page transitions in Next.js) and
  // includes any query parameters attached to the URL.
  const pathAndQueryString = context.resolvedUrl;

  // Combine to get the full URL
  return `${protocol}://${host}${pathAndQueryString}`;
}

export default function Index({
  status,
  call,
}: {
  callSlug: string;
  status: "upcoming" | "closed" | "draft";
  call: NonNullable<TGetCallBySlug["call"]>;
}) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.index`);
  const format = useFormatter();
  const documentTitle = t(`${status}DocumentTitle`);
  const title =
    status === "upcoming"
      ? t("upcomingTitle", {
          date: format.dateTime(new Date(call.submissionStartDate), {
            day: "numeric",
            month: "long",
          }),
        })
      : t(`${status}Title`);
  const description =
    status === "upcoming"
      ? t("upcomingDescription", {
          title: call.title.en,
          date: format.dateTime(new Date(call.submissionStartDate), {
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
          time: format.dateTime(new Date(call.submissionStartDate), {
            hour: "numeric",
            minute: "numeric",
          }),
        })
      : t(`${status}Description`, {
          title: call.title.en,
        });

  return (
    <>
      <Head>
        <title key="title">{docTitle(documentTitle, call.title.en)}</title>
      </Head>
      <Container className="max-w-2xl p-8 min-h-screen relative mx-auto flex flex-col justify-center items-center">
        <div className="flex flex-col my-7 py-4 justify-center items-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={call.logoDefault as string}
            className="object-contain max-w-[180px] max-h-[80px]"
            alt={call.title.en}
          />
        </div>
        <Text as="h1" weight="bold" size="2xl" className="py-4 text-center">
          {title}
        </Text>
        <Text
          as="p"
          className="text-center leading-relaxed tracking-wide"
          color="muted"
        >
          {description}
        </Text>

        <div className="flex gap-4 flex-col my-3 py-2 w-full sm:flex-row justify-center items-center">
          {status !== "closed" && call.callPages.length > 0 && (
            <Link
              className="w-full md:w-auto"
              href={urls.callPage(call.callPages[0].slug)}
            >
              <Button className="w-full" size="base" variant="secondary">
                {t("aboutThisCallBtn")}
              </Button>
            </Link>
          )}
          <Link className="w-full md:w-auto" href={urls.index}>
            <Button className="w-full" size="base" variant="primary">
              {t("exploreOpportunitiesBtn")}
            </Button>
          </Link>
        </div>

        <div className="my-10 flex flex-col gap-12 justify-center items-center">
          <PoweredBy />
        </div>
      </Container>
    </>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const callSlug = getCallSlug(context);
  const { call } = await getCallBySlug(callSlug);
  const messages = await bundleMessages(context.locale, [
    `${constants.MESSAGE_PREFIX}pages.index`,
    ...PoweredBy.messages,
  ]);

  // Redirect to 404 if the call is not found
  if (!call?.id) {
    return {
      notFound: true,
    };
  }

  // check if the user has a jwt cookie, if they don't redirect to login
  const jwt = getJwt(context);
  if (!jwt) {
    // Show the status page immediately — unauthenticated users shouldn't need
    // to log in just to see that a call is upcoming or closed.
    const callStatus = getCallStatus(call);
    if (callStatus) {
      return { props: { call, status: callStatus, messages } };
    }

    // Get current location from the context object to redirect to the same page
    // after login
    const fullUrl = getCurrentUrl(context);
    const queryParams = new URLSearchParams({ redirectTo: fullUrl });

    return {
      redirect: {
        destination: urls.submissionLogin(call.id, queryParams),
      },
    };
  }

  // get the current user data from the token payload
  const { payload: currentUser } = JWT.decode(jwt) as {
    header: Record<string, string>;
    payload: {
      id: string;
      scope: string[];
    };
  };

  // Draft calls aren't published yet: only call managers may submit to them,
  // to test the call before it goes live.
  if (call.visibilityStatus === "draft") {
    const isCallManager = await getIsCallManager(call.id, jwt);
    if (!isCallManager) {
      return { props: { call, status: "draft", messages } };
    }
  }

  // Create a GrowthBook client instance
  const growthbook = new GrowthBook({
    attributes: {
      id: currentUser.id,
      employee: currentUser.scope.includes("employee"),
      callId: call.id,
    },
    features: await getFeatures(),
    trackingCallback: (exp, res) => {
      console.info("In Experiment", exp.key, res.variationId);
    },
  });

  // Check if the user is in the experiment
  if (!growthbook.isOn("new-submission-wizard")) {
    // prepare the old/legacy wizard with contactAgreed=true
    const url = new URL(
      `${process.env.NEXT_PUBLIC_SUBMISSION_APP_URL}/contests/${call?.id}/start?contactAgreed=true`,
    );

    // Append all query parameters from the current URL to the old wizard URL
    // so we don't lose any query parameters (e.g. utm parameters)
    Object.entries(context.query).forEach(([key, value]) => {
      url.searchParams.append(key, value as string);
    });

    // The callSlug query parameter is added by Next.js (because of the route paramter)
    // and should be removed before redirecting to the old wizard.
    url.searchParams.delete("callSlug");

    return {
      redirect: {
        // redirect to the old/legacy wizard
        destination: url.toString(),
      },
    };
  }

  // Attempt to create a submission regardless of the call's date window.
  // Users with special submission permissions (e.g. invited applicants, org
  // members) are allowed to submit to upcoming calls — the backend is the
  // authoritative gate. Draft calls are only open to call managers (checked
  // above). We only check dates if the backend rejects the
  // request, to show a helpful status page instead of a generic error.
  let submission: TCreateSubmission["submission"];
  try {
    const result = await createSubmission(call.id, {
      Authorization: `Bearer ${jwt}`,
      ...convertUtmParamsToHeaders(context.query),
    });
    submission = result?.submission;
  } catch (err) {
    const callStatus = getCallStatus(call);
    if (callStatus) {
      return { props: { call, status: callStatus, messages } };
    }
    throw err;
  }

  if (!submission) {
    return { statusCode: 500 };
  }

  // If they have a finished submission
  if (submission.status === "SUBMITTED") {
    // let them edit their submission if the call allows it
    // and if the call is still open
    const isEditable = checkIsCallEditable(call);
    if (isEditable) {
      return {
        redirect: {
          destination: `/${callSlug}/profile`,
        },
      };
    }

    // otherwise redirect them to the success page
    return {
      redirect: {
        destination: `/${callSlug}/success`,
      },
    };
  }

  // If they have a submission, redirect to the last step of the submission
  // they were working on
  if (submission.status === "DRAFT") {
    // QUESTION: How do we know which step they were on
    // TODO: fix this to redirect to the last step
    // The backend approaches:
    // - backend can dynamically determine the last step based on the submission data state
    // - backend can save lastStep provided by the frontend (could make sense if dynamic
    //   determination based on data isn't sufficient or too tricky)
    const redirectTo = "profile";
    return {
      redirect: {
        destination: `/${callSlug}/${redirectTo}`,
      },
    };
  }

  return {
    props: {
      callSlug,
      status: "closed",
    },
  };
}

function getCallStatus(
  call: NonNullable<TGetCallBySlug["call"]>,
): "upcoming" | "closed" | null {
  if (new Date(call.submissionStartDate) > new Date()) return "upcoming";
  if (new Date(call.submissionEndDate) < new Date()) return "closed";
  return null;
}

function createSubmission(
  callId: string,
  requestHeaders?: HeadersInit,
): Promise<TCreateSubmission> {
  return graphqlFetch(
    JSON.stringify({
      query: CreateSubmission,
      variables: { callId },
    }),
    requestHeaders,
  );
}

async function getIsCallManager(callId: string, jwt: string) {
  try {
    const { callAccess }: TGetCallAccess = await graphqlFetch(
      JSON.stringify({
        query: GetCallAccess,
        variables: { callId, scopes: ["owner", "manager"] },
      }),
      { Authorization: `Bearer ${jwt}` },
    );
    return callAccess;
  } catch (err) {
    // Fail closed: without a confirmed manager role, treat the user as a
    // regular applicant
    console.error("Failed to check call access", err);
    return false;
  }
}

async function getCallBySlug(callSlug: string): Promise<TGetCallBySlug> {
  return await graphqlFetch(
    JSON.stringify({
      query: GetCallBySlug,
      variables: { slug: callSlug },
    }),
  );
}

function getCallSlug(context: GetServerSidePropsContext) {
  const { callSlug } = context.params ?? {};

  if (Array.isArray(callSlug)) {
    return callSlug[0] as string;
  }

  return callSlug as string;
}

function getJwt(context: GetServerSidePropsContext) {
  const { cookie: cookies } = context.req.headers;
  if (!cookies) {
    return null;
  }

  const { jwt } = cookie.parse(cookies);

  return jwt;
}

/**
 * Extracts the UTM parameters from the query string and converts them to
 * custom request headers (e.g. utm_source -> UTM-Source)
 */
function convertUtmParamsToHeaders(
  queryParams: Record<string, string | string[] | undefined>,
) {
  const utmParams = {
    "UTM-Source": queryParamToString(queryParams.utm_source),
    "UTM-Medium": queryParamToString(queryParams.utm_medium),
    "UTM-Campaign": queryParamToString(queryParams.utm_campaign),
    "UTM-Term": queryParamToString(queryParams.utm_term),
    "UTM-Content": queryParamToString(queryParams.utm_content),
  } as const;

  // Filter any undefined values, since undefined is not a valid header value
  return Object.fromEntries(
    Object.entries(utmParams).filter(
      // This type assertion is necessary because TypeScript doesn't understand
      // that we've filtered out the undefined values. entry[1] is the entry value.
      (entry): entry is [string, string] => entry[1] !== undefined,
    ),
  );
}

// Convert query param to string or undefined
function queryParamToString(param: string | string[] | undefined) {
  // If a query param is set mutliple times, it will be parsed as array.
  // For the sake of simplicity, we'll just use the first value.
  if (Array.isArray(param)) {
    return param[0];
  }
  return param;
}
